import { __decorate, __metadata } from 'tslib';
import SegmentedControl from '@tencent/dui/lib/components/SegmentedControl';
import Tabs from '@tencent/dui/lib/components/Tabs';
import { EnvContext, Inject, withState, BuiltinUiState, evaluate } from '@tencent/workbench';
import classNames from 'classnames';
import React, { Component } from 'react';
import { FormService } from '../services/form-service.js';
import { FormAccordion } from './form-accordion.js';
import { AngleFormItem } from './form-items/angle/angle.js';
import { FormButton } from './form-items/button/form-button.js';
import { CheckboxFormItem } from './form-items/checkbox/checkbox.js';
import { ColorPickerPanel } from './form-items/color-pick-panel/color.js';
import { SelectFormItem } from './form-items/select/select.js';
import { SliderFormItem } from './form-items/slider/slider.js';
import { SpinInputFormItem } from './form-items/spin/spin.js';
import { TextFormItem } from './form-items/text/text.js';
import styles from './form.less.js';

const EMPTY_ITEMS = ['space', 'pusher', 'break'];
class FormContainerComponent extends Component {
    constructor() {
        super(...arguments);
        this.id = Math.random().toString(36)
            .substring(2, 8);
        this.getSequenceInfo = (index, array) => {
            var _a, _b;
            const first = index === 0 || EMPTY_ITEMS.includes((_a = array[index - 1]) === null || _a === void 0 ? void 0 : _a.name);
            const last = index === array.length - 1 || EMPTY_ITEMS.includes((_b = array[index + 1]) === null || _b === void 0 ? void 0 : _b.name);
            const wrappedBefore = array.slice(0, index).map(i => i.name)
                .includes('break');
            const wrappedAfter = array.slice(index + 1).map(i => i.name)
                .includes('break');
            return {
                first,
                last,
                wrappedBefore,
                wrappedAfter,
            };
        };
        this.notHide = (item) => {
            const hide = evaluate(item.shouldHide, false);
            return !hide;
        };
        this.getComponent = (item, index, array) => {
            var _a, _b;
            let component;
            const disabled = evaluate(item.shouldDisabled, false);
            const style = evaluate(item.style, {});
            const wrapperClasses = Object.entries(this.getSequenceInfo(index, array)).reduce((acc, [key, value]) => {
                if (value) {
                    acc.push(`form-row-item-${key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase()}`);
                }
                return acc;
            }, [styles['form-row-item']]);
            let useSelfDisabledBehavior = false;
            switch (item.type) {
                case 'slider':
                    component = React.createElement(SliderFormItem, Object.assign({}, item));
                    break;
                case 'color':
                    component = React.createElement(ColorPickerPanel, Object.assign({}, item));
                    break;
                case 'spin':
                    component = React.createElement(SpinInputFormItem, Object.assign({}, item));
                    break;
                case 'angle':
                    component = React.createElement(AngleFormItem, Object.assign({}, item));
                    break;
                case 'button':
                    component = React.createElement(FormButton, Object.assign({}, item, { disabled: disabled }));
                    useSelfDisabledBehavior = true;
                    break;
                case 'select':
                    component = React.createElement(SelectFormItem, Object.assign({}, item));
                    break;
                case 'checkbox':
                    component = React.createElement(CheckboxFormItem, Object.assign({}, item));
                    break;
                case 'text':
                    component = React.createElement(TextFormItem, Object.assign({}, item));
                    break;
                case 'custom':
                    component = evaluate(item.content, null);
                    break;
                case 'builtin': return React.createElement("div", { key: `${item.id}-${index}`, className: [
                        styles['form-row-item'],
                        styles['form-row-item-builtin'],
                        styles[`form-row-item-${item.name}`],
                    ].join(' ') });
                default:
                    component = null;
                    break;
            }
            return React.createElement("div", { key: `${item.id}-${index}`, className: wrapperClasses.join(' '), "aria-label": (_b = (_a = item.label) !== null && _a !== void 0 ? _a : item.ariaLabel) !== null && _b !== void 0 ? _b : '', style: Object.assign(Object.assign({}, style), (disabled && !useSelfDisabledBehavior ? { opacity: 0.3, pointerEvents: 'none' } : {})), onMouseDown: (e) => {
                    if (!item.preventFocus) {
                        e.stopPropagation();
                    }
                } }, component);
        };
    }
    componentDidMount() {
        this.formService.newContainer(this.id);
    }
    componentWillUnmount() {
        this.formService.closeContainer(this.id);
    }
    renderSection(section, groupName) {
        var _a, _b;
        if (section.name && evaluate(section.shouldHide, false)) {
            return null;
        }
        const defaultCollapsed = (_a = this.formService.getCollapsed(section.label)) !== null && _a !== void 0 ? _a : section.defaultCollapsed;
        const defaultOpen = defaultCollapsed === undefined ? undefined : !defaultCollapsed;
        const lastIndex = this.getLastIndex(section.items);
        return React.createElement(FormAccordion, { key: section.label, icon: section.collapsable !== false ? React.createElement("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.84 7.787a.2.2 0 0 0 .32 0l2.6-3.467A.2.2 0 0 0 8.6 4H3.4a.2.2 0 0 0-.16.32l2.6 3.467Z", fill: "currentColor" })) : undefined, name: groupName, defaultOpen: defaultOpen, title: section.label, action: evaluate(section.content, null), showSwitch: section.showSwitch, collapsable: (_b = section.collapsable) !== null && _b !== void 0 ? _b : true, style: this.props.accordionStyle, onCollapse: (collapsed) => {
                this.formService.setCollapsed(section.label, collapsed);
            } }, section.items.map((item, index) => {
            const key = `form-${section.label}-${index}`;
            const itemClassName = classNames({
                [styles['form-row']]: true,
                [styles['form-row-last']]: index === lastIndex,
            });
            if (Array.isArray(item)) {
                return React.createElement("div", { className: itemClassName, key: key }, item.filter(this.notHide).map(this.getComponent));
            }
            if (this.notHide(item)) {
                return React.createElement("div", { className: itemClassName, key: key }, this.getComponent(item, 0, [item]));
            }
            return null;
        }));
    }
    getLastIndex(arr) {
        const { length } = arr;
        const reversedArray = [...arr].reverse();
        let lastIndex = length - 1;
        for (let index = 0; index < length; index += 1) {
            if (this.notHide(reversedArray[index])) {
                lastIndex -= index;
                return lastIndex;
            }
        }
        return lastIndex;
    }
    changeForm(id) {
        var _a, _b, _c;
        const activeForm = (_a = this.context.store.getValue('activeForm')) !== null && _a !== void 0 ? _a : {};
        activeForm[this.id] = id;
        this.context.store.update('activeForm', Object.assign({}, activeForm));
        (_c = (_b = this.props).onFormChange) === null || _c === void 0 ? void 0 : _c.call(_b, id);
    }
    render() {
        var _a, _b, _c;
        const activeForm = (_a = this.props.activeForm[this.id]) !== null && _a !== void 0 ? _a : '';
        const form = (_c = (_b = this.props.form.find(f => f.id === activeForm)) !== null && _b !== void 0 ? _b : this.props.form.find(f => f.id === this.props.forms[0])) !== null && _c !== void 0 ? _c : this.props.form[0];
        if (!form)
            return null;
        let activeTab;
        let explicitTabs;
        if (form.tabs) {
            // 对props传过来的activeTab进行校验
            explicitTabs = form.tabs.filter(this.notHide);
            const explicitTabIds = explicitTabs.map((tab, idx) => `tab-${idx}`);
            const prosActiveTabId = this.props.activeTab[this.id];
            activeTab = explicitTabIds.includes(prosActiveTabId) ? prosActiveTabId : '';
        }
        return React.createElement("div", { className: styles.container },
            !this.props.headless && React.createElement(SegmentedControl, { defaultActiveId: form.id, className: styles.panels, segmentClassName: styles.panel, onChange: id => this.changeForm(id), activeId: form.id }, this.props.forms.map(id => this.props.form.find(f => f.id === id))
                .filter(Boolean)
                .map((item) => {
                const config = this.formService.get(item.id);
                const disabled = evaluate(config.shouldDisabled, false);
                return React.createElement(SegmentedControl.Segment, { key: item.id, id: item.id, style: disabled ? { pointerEvents: 'none', opacity: 0.3 } : {} }, item.label);
            })),
            form.tabs && React.createElement(Tabs, { alignment: "left", tabBarClassName: styles.tabs, tabBarItemClassName: styles.tab, indicatorClassName: styles.indicator, activeId: form.tabs.some((tab, i) => `tab-${i}` === activeTab) ? activeTab : 'tab-0', className: styles['tabs-container'], style: { height: 'calc(100% - 66.5px)' }, contentStyle: {
                    maxHeight: 'calc(100% - 36px)',
                    overflow: 'auto', scrollbarGutter: 'stable',
                    paddingLeft: 16, paddingRight: 8,
                }, indicatorWidth: this.props.indicatorWidth, indicatorStyle: this.props.indicatorStyle, tabBarSize: this.props.tabBarSize, onChange: (tab) => {
                    var _a, _b, _c, _d;
                    const tabs = (_a = this.context.store.getValue('activeTab')) !== null && _a !== void 0 ? _a : {};
                    tabs[this.id] = tab;
                    this.context.store.update('activeTab', Object.assign({}, tabs));
                    const label = (_b = form.tabs[Number(tab.slice(-1))]) === null || _b === void 0 ? void 0 : _b.label;
                    (_d = (_c = this.props).onTabChange) === null || _d === void 0 ? void 0 : _d.call(_c, tab, label);
                } }, explicitTabs.map((tab, i) => React.createElement(Tabs.TabPane, { id: `tab-${i}`, key: tab.label, tab: tab.label }, tab.sections.map(section => this.renderSection(section, `tab-${i}`))))),
            form.sections && React.createElement("div", { style: { maxHeight: '100%', overflow: 'auto' } }, form.sections.map(section => this.renderSection(section, form.id))));
    }
}
FormContainerComponent.contextType = EnvContext;
__decorate([
    Inject(),
    __metadata("design:type", FormService)
], FormContainerComponent.prototype, "formService", void 0);
/**
 * @public
 */
const FormContainer = withState([
    'form',
    'activeForm',
    'activeTab',
    BuiltinUiState.SelectionState,
    BuiltinUiState.ModelState,
])(FormContainerComponent);

export { FormContainer };
//# sourceMappingURL=form-container.js.map
